import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

/**
 * Helper function to get CSRF token from cookies
 */
const getCsrfTokenFromCookie = () => {
  const name = 'XSRF-TOKEN';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const token = parts.pop().split(';').shift();
    return decodeURIComponent(token);
  }
  return null;
};

/**
 * Centralized API instance for all services
 * Uses Bearer token authentication (primary) + CSRF token when cookie is present (for login/register)
 */
export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000, // 30 second timeout
  withCredentials: true, // Required: send cookies (XSRF-TOKEN) so Laravel CSRF validation can match header
});

/**
 * Request interceptor - Add Bearer token and CSRF token to all requests
 */
apiClient.interceptors.request.use(
  (config) => {
    try {
      // Add Bearer token (primary authentication)
      const token = localStorage.getItem('authToken');
      if (token && token.length > 0) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Add CSRF token from cookie (Laravel CSRF protection)
      const csrfToken = getCsrfTokenFromCookie();
      if (csrfToken && csrfToken.length > 0) {
        config.headers['X-CSRF-TOKEN'] = csrfToken;
      }
      
      return config;
    } catch (error) {
      console.error('Request interceptor error:', error);
      return config;
    }
  },
  (error) => {
    console.error('Request interceptor rejection:', error);
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - Handle errors and 401 responses
 */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      // Handle 401 Unauthorized (token expired or invalid)
      if (error.response?.status === 401) {
        const currentPath = window.location.pathname;
        
        // Only redirect if not already on auth pages
        const isAuthPage = currentPath.includes('/login') || 
                          currentPath.includes('/register') ||
                          currentPath === '/';
        
        if (!isAuthPage) {
          console.warn('Token expired, redirecting to login');
          
          // Clear stored auth data
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          
          // Redirect to login page
          window.location.href = '/login';
        }
      }
      
      // Handle network errors
      if (error.message === 'Network Error' || !error.response) {
        console.error('Network error:', error);
      }
    } catch (err) {
      console.error('Response interceptor error:', err);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
